// usePersistentState — useState that survives component remounts by mirroring
// to localStorage. 2026-05-30: worker map filter settings (region, learner
// filter, found/unfound, show-learners, all-placers) reset every time the
// worker switched tabs (route change unmounts MapMark). This keeps them.
//
// Drop-in for useState: returns [value, setValue]. Optional serialize/
// deserialize for non-JSON-native types (e.g. Set<string>). On any
// localStorage failure it degrades to plain in-memory state (never throws).

import { useCallback, useRef, useState } from "react";

export interface PersistOptions<T> {
  /** Convert T → a JSON-serializable value before storing. Default: identity. */
  serialize?: (value: T) => unknown;
  /** Convert the parsed JSON back → T on load. Default: identity. */
  deserialize?: (raw: unknown) => T;
}

export function usePersistentState<T>(
  key: string,
  initialValue: T,
  options: PersistOptions<T> = {},
): [T, (value: T | ((prev: T) => T)) => void] {
  const { serialize, deserialize } = options;

  const [value, setValueState] = useState<T>(() => {
    if (typeof localStorage === "undefined") return initialValue;
    try {
      const raw = localStorage.getItem(key);
      if (raw === null) return initialValue;
      const parsed = JSON.parse(raw);
      return deserialize ? deserialize(parsed) : (parsed as T);
    } catch {
      return initialValue;
    }
  });

  // Keep the latest value in a ref so updater-functions resolve against the
  // current value when we serialize, without adding `value` to the callback
  // deps (which would make setValue identity churn every render).
  const valueRef = useRef(value);
  valueRef.current = value;

  const setValue = useCallback(
    (next: T | ((prev: T) => T)) => {
      const resolved =
        typeof next === "function"
          ? (next as (prev: T) => T)(valueRef.current)
          : next;
      setValueState(resolved);
      try {
        const toStore = serialize ? serialize(resolved) : resolved;
        localStorage.setItem(key, JSON.stringify(toStore));
      } catch {
        /* localStorage unavailable / full — keep the in-memory value only. */
      }
    },
    [key, serialize],
  );

  return [value, setValue];
}
